<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="connection">
					<app-heading :title="isNew ? 'New Connection' : connection.name" />
					<app-form @submit="onSubmit">
						<app-section style="width: 500px;">
							<app-section-actions>
								<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
								<app-button type="submit" small color="primary" v-else>Save</app-button>
								<app-button
									type="cancel"
									small
									:to="{ name: 'integration-child', params: {
										integration: integrationId,
										tab: 'connections',
									}}"
								>Cancel</app-button>
							</app-section-actions>
							<input-text
								type="text"
								label="Name"
								v-model="connection.name"
								:errors="errors.name"
								required
							/>
							<input-select
								label="Synchronization type"
								:items="syncTypes"
								v-model="connection.sync_type"
								:errors="errors.sync_type"
							/>

							<input-select
								label="Enterprise"
								v-model="connection.enterprise"
								:items="enterprises"
								:errors="errors.enterprise"
							/>
							<input-select
								label="Converge identity"
								:items="converge_identities"
								v-model="connection.converge"
								:errors="errors.converge"
							/>
							<div v-for="(data, field) in integration.auth" :key="`auth-${field}`">
								<input-combobox v-if="data.multiple"
									:label="data.name"
									v-model="connection.auth[field]"
									:errors="errors.name"
									multiple
									can-add
									no-selection
								/>
								<input-text v-else
									:type="data.encrypted ? 'password' : 'text'"
									:label="data.name"
									v-model="connection.auth[field]"
									:errors="errors.name"
									required
								/>
							</div>
						</app-section>
					</app-form>
				</app-tab-item>

				<app-tab-item :tab="tab" value="commissions">
					<app-heading :title="`${connection.name} Commissions`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('integration.post')"
								color="success"
								small
								outlined
								:to="{ name: 'integration-commission', params: {
									integration: integrationId,
									connection: id,
									commission: 'new',
								}}"
							>Add Commission</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="commissions.items"
								:total="commissions.count"
								:refresh.sync="commissionRefresh"
								:headers="commissions.headers"
								@update="fetchCommissions"
							>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('connection.put')"
										color="secondary"
										:to="{ name: 'integration-commission', params: {
											integration: integrationId,
											connection: id,
											commission: item._id,
										}}"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('integration.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove ` +
											`commission from source <b>${item.source}</b>?`"
										title="Remove Commission"
										@click="onCommissionDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<app-tab-item :tab="tab" value="overrides">
					<app-heading :title="`${connection.name} Channel Overrides`" />
					<app-row>
						<app-cell>
							<app-data-table
								:dynamic="false"
								:items="overrides.items"
								:total="overrides.count"
								:headers="overrides.headers"
							>
								<template v-slot:converge="{ item }">
									<input-select
										label="Converge identity (Override)"
										:items="converge_identities"
										clearable
										v-model="item.converge"
										@change="onOverrideConvergeIdentityChange(item._id, item.channel, $event)"
									/>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { reduce } from 'lodash'

import Tab from '@/types/Tab'
import DataTable from '@/types/DataTable'

import { mapSelectOptions, mapEntries } from '@/utils/maps'

const ENCRYPTED_SYMBOL = '__3NC__'

export default {
	async created() {
		try {
			this.$store.dispatch('app/startLoading')

			// Get integration
			this.integration = await this.$ravello.request()
				.v(2)
				.to('integrations')
				.to(this.integrationId)
				.get()

			// Update authentication object
			this.connection.auth = reduce(this.integration.auth, (acc, data, field) => {
				acc[field] = ''
				return acc
			}, {})

			if (this.id !== 'new') {
				const connection = await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.integrationId)
					.to('connections')
					.to(this.id)
					.get()

				this.connection = {
					name: connection.name,
					auth: reduce(connection.auth, (acc, value, field) => {
						if (this.integration.auth[field]) {
							if (this.integration.auth[field].multiple) {
								acc[field] = value.split(',')
							} else {
								acc[field] = this.integration.auth[field].encrypted ? ENCRYPTED_SYMBOL : value
							}
						}
						return acc
					}, {}),
					enterprise: connection.enterprise,
					channels: connection.channels,
					converge: connection.converge,
					sync_type: connection.sync_type,
				}

				const channels = connection.channels.length ? await this.$ravello.request()
					.v(2)
					.to('channels')
					.param('_id', connection.channels)
					.fields(['name', 'resort'])
					.limit(connection.channels.length)
					.get() : []

				const resortIds = Object.keys(channels.reduce((acc, channel) => ({ ...acc, [channel.resort]: true }), {}))
				const resortsMap = resortIds.length ? mapEntries(await this.$ravello.request()
					.v(2)
					.to('resorts')
					.param('_id', resortIds)
					.fields(['name'])
					.limit(resortIds.length)
					.get()) : {}

				const overridesMap = channels.length ? mapEntries(await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.integrationId)
					.to('connections')
					.to(this.id)
					.to('overrides')
					.param('channel', channels.map(({ _id }) => _id))
					.fields(['converge', 'channel'])
					.limit(channels.length)
					.get(), { key: 'channel' }) : {}

				this.overrides.items = channels.map((channel) => {
					const override = overridesMap[channel._id]
					const resort = resortsMap[channel.resort]

					return {
						name: channel.name,
						resort_name: resort.name,
						channel: channel._id,
						_id: override ? override._id : null,
						converge: override ? override.converge : null,
					}
				})
			}

			// Initialize tabs
			this.tabs = [
				new Tab('connection', {
					to: {
						name: 'integration-connection',
						params: {
							integration: this.integrationId,
							connection: this.id,
						},
					},
					name: 'Connection',
					exact: true,
					value: `/integration/${this.integrationId}/connections/${this.id}`,
				}),
				new Tab('commissions', {
					to: {
						name: 'integration-connection-child',
						params: {
							integration: this.integrationId,
							connection: this.id,
							connectiontab: 'commissions',
						},
					},
					name: 'Commissions',
					disabled: this.isNew,
					value: `/integration/${this.integrationId}/connections/${this.id}/commissions`,
				}),
				new Tab('overrides', {
					to: {
						name: 'integration-connection-child',
						params: {
							integration: this.integrationId,
							connection: this.id,
							connectiontab: 'overrides',
						},
					},
					name: 'Channel Overrides',
					disabled: this.isNew,
					value: `/integration/${this.integrationId}/connections/${this.id}/overrides`,
				}),
			]

			// Initalize converge identities
			// @todo - add and use the datasource endpoint if relevent
			this.converge_identities = mapSelectOptions(await this.$ravello.request()
				.v(2)
				.to('converge')
				.limit(100)
				.get())

			// Load datasources
			this.enterprises = await this.$ravello.graphql('enterprises')
				.sort('name')
				.datasource()
				.query()

			this.$store.dispatch('app/stopLoading')
		} catch (err) {
			this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
		}
	},
	data() {
		return {
			integration: {},
			connection: {
				name: '',
				auth: {},
				enterprise: null,
				integration: null,
				channels: [],
				converge: null,
				sync_type: 1,
			},
			syncTypes: [{
				text: 'All',
				value: 1,
			}, {
				text: 'Rates & Availabilities',
				value: 2,
			}],
			tabs: [],
			enterprises: [],
			commissions: new DataTable([{
				text: 'Source',
				value: 'source',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}]),
			overrides: new DataTable([{
				text: 'Resort',
				value: 'resort_name',
				sortable: true,
				width: '30%',
			}, {
				text: 'Channel',
				value: 'name',
				sortable: true,
				width: '30%',
			}, {
				text: 'Converge Identity',
				value: 'converge',
				align: 'end',
				sortable: false,
			}]),
			commissionRefresh: false,
			errors: {},
			converge_identities: [],
		}
	},
	computed: {
		integrationId() {
			return this.$route.params.integration
		},
		id() {
			return this.$route.params.connection
		},
		isNew() {
			return this.id === 'new'
		},
	},
	methods: {
		getConnectionAuthData() {
			return {
				...this.connection,
				auth: reduce(this.connection.auth, (acc, value, field) => {
					if (value !== ENCRYPTED_SYMBOL) {
						acc[field] = value
					}
					if (Array.isArray(value)) {
						acc[field] = value.join(',')
					}
					return acc
				}, {}),
			}
		},

		async onSubmit() {
			this.$store.dispatch('app/startLoading')
			try {
				if (this.isNew) {
					await this.$ravello.request()
						.v(2)
						.to('integrations')
						.to(this.integrationId)
						.to('connections')
						.create(this.getConnectionAuthData())
				} else {
					await this.$ravello.request().to('integrations')
						.v(2)
						.to(this.integrationId)
						.to('connections')
						.to(this.id)
						.update(this.getConnectionAuthData())
				}
				this.$store.dispatch('app/stopLoading')
				this.$router.push({
					name: 'integration-child',
					params: {
						integration: this.integrationId,
						tab: 'connections',
					},
				})
				this.$notifications.pushAlert('success', `Connection successfully ${this.isNew ? 'added' : 'updated'}`)
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}

				this.$store.dispatch('app/stopLoading')
			}
		},

		async fetchCommissions(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('integrations')
				.to(this.integrationId)
				.to('connections')
				.to(this.id)
				.to('commissions')
				.fromPagination(pagination)

			try {
				this.commissions.items = await request.get()
				if (this.commissions.count === -1) {
					const count = await request.count()
					this.commissions.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onCommissionDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.integrationId)
					.to('connections')
					.to(this.id)
					.to('commissions')
					.to(id)
					.delete()

				this.$notifications.pushAlert('success', 'Commission successfully deleted')
				this.commissionRefresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onOverrideConvergeIdentityChange(id, channel, identity) {
			this.$store.dispatch('app/startLoading')
			try {
				const request = this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.integrationId)
					.to('connections')
					.to(this.id)
					.to('overrides')

				if (id) {
					await request.to(id).update({
						converge: identity,
					})
				} else {
					const override = await request.to(id).create({
						channel,
						converge: identity,
					})
					const i = this.overrides.items.findIndex(({ channel: overrideChannel }) => channel === overrideChannel)
					if (i !== -1) {
						this.overrides.items[i]._id = override._id
					}
				}

				console.log(this.overrides.items)
				this.$notifications.pushAlert('success', 'Converge identity override successfully updated')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
